import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import PublicIcon from '@mui/icons-material/Public';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

const BlitzT2Landing = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <SpeedIcon sx={{ fontSize: 40 }} />,
      title: 'Instant Settlement',
      description: 'Transfer value globally in under 500ms with near-zero transaction costs'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: 'USDC-Backed Infrastructure',
      description: 'Every dollar is 1:1 backed by USDC in qualified custody with full auditability'
    },
    {
      icon: <PublicIcon sx={{ fontSize: 40 }} />,
      title: 'Global Accessibility',
      description: 'Financial infrastructure for creators, freelancers, and businesses worldwide'
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40 }} />,
      title: 'Programmable Treasury',
      description: 'Automated treasury operations with real-time liquidity management'
    }
  ];

  const balanceComponents = [
    { component: 'Ledger ID', description: 'Hybrid identifier connecting KYC records to custodian sub-accounts' },
    { component: 'Denomination', description: 'USD display, fully backed by USDC at 1:1 ratio' },
    { component: 'State Variables', description: 'Available Balance, Held Collateral, Pending Settlement' },
    { component: 'Events', description: 'Deposit, Purchase, Redemption, Transfer, Hold, Reward' }
  ];

  const revenueStreams = [
    { stream: 'Trading Spread', description: 'USDC buy/sell operations (15–25 bps per side)', spread: '0.3–0.5%' },
    { stream: 'Interchange Revenue', description: 'Card transaction processing fees', spread: '0.6–1.0%' },
    { stream: 'Custody Float Yield', description: 'Short-term T-Bill yields on fiat settlement buffers', spread: '2–4% APY' },
    { stream: 'FX Services', description: 'Local currency conversion margins', spread: '0.5–1.0%' }
  ];

  const integrations = [
    { product: 'BlitzPay', function: 'Instant invoice liquidity', interface: 'Auto-credit Blitz Balance' },
    { product: 'Blitz Treasury', function: 'Receivables financing & cash flow', interface: 'Unified ledger' },
    { product: 'Blitz Global Wallets', function: 'Contractor/creator payouts', interface: 'USDC sub-accounts' },
    { product: 'BlitzPay Power Card', function: 'Global spend & rewards', interface: 'Collateralized credit' }
  ];

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
          color: 'white',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" gutterBottom fontWeight="bold">
            Blitz T2
          </Typography>
          <Typography variant="h5" sx={{ mb: 2, opacity: 0.95 }}>
            Transmitter & Treasury Layer
          </Typography>
          <Typography variant="h6" sx={{ mb: 4, maxWidth: '800px', mx: 'auto', opacity: 0.9, lineHeight: 1.6 }}>
            A unified financial infrastructure designed to abstract away the complexity of global value movement and custodial treasury management
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              onClick={() => navigate('/onboard')}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
              onClick={() => window.open('https://docs.google.com/document/d/1wBuCrNiCMDcwq2m4KxXHwAlqvV8jxp-AfYVJzVS2sB0/edit?usp=sharing', '_blank')}
            >
              Technical Documentation
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Founder Section */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: 5, 
            mb: 6,
            borderLeft: '5px solid',
            borderColor: 'primary.main',
            bgcolor: 'grey.50'
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
            Leadership
          </Typography>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Avatar
                src="https://storage.googleapis.com/blitzccmc.appspot.com/1/IMG_3232.JPG"
                alt="Alex Roa"
                sx={{
                  width: 180,
                  height: 180,
                  border: '4px solid',
                  borderColor: 'primary.main',
                  boxShadow: 2
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Alex Roa
              </Typography>
              <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                Founder & CEO/CTO
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
                Alex Roa is building the next generation of global financial infrastructure. 
                With deep technical expertise in distributed systems and financial technology, 
                Alex leads Blitz in revolutionizing how value moves across borders—creating 
                programmable, instant, and inclusive financial rails for the digital economy.
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Abstract Section */}
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <Typography variant="h4" gutterBottom color="primary" fontWeight="bold">
            Abstract
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            Blitz T2 (Transmitter & Treasury Layer) is a unified financial infrastructure designed 
            to abstract away the complexity of global value movement. Built on USDC-denominated ledger 
            systems, it enables instant low-cost transfers, on-demand liquidity, programmatic treasury 
            operations, and secured credit issuance—all without requiring Blitz itself to assume the 
            legal role of a money transmitter. By combining custodial sub-accounts, programmable balance 
            states, and automated USDC acquisition/redemption, Blitz T2 forms the monetary foundation 
            for BlitzPay, Blitz Wallets, and the BlitzPay Power Card.
          </Typography>
        </Paper>

        {/* Features Grid */}
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          Core Capabilities
        </Typography>
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  p: 3,
                  transition: 'all 0.3s',
                  borderTop: '3px solid transparent',
                  '&:hover': { 
                    transform: 'translateY(-4px)',
                    borderTopColor: 'primary.main',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent>
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* The Problem */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, borderLeft: '5px solid', borderColor: 'error.main' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            The Challenge
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            Despite a globally connected digital economy, money remains constrained by legacy infrastructure:
          </Typography>
          <Box component="ul" sx={{ pl: 3, '& li': { mb: 2 } }}>
            <li>
              <Typography variant="body1" color="text.secondary">
                Cross-border transfers require multi-day settlement and carry 3–7% fees
              </Typography>
            </li>
            <li>
              <Typography variant="body1" color="text.secondary">
                Freelancers, creators, and contractors lack reliable global banking access
              </Typography>
            </li>
            <li>
              <Typography variant="body1" color="text.secondary">
                Businesses must maintain fragmented rails and region-specific compliance stacks
              </Typography>
            </li>
            <li>
              <Typography variant="body1" color="text.secondary">
                Credit underwriting relies on slow, collateral-heavy systems and manual verification
              </Typography>
            </li>
          </Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 3 }}>
            The result: friction, exclusion, and high latency in a world that increasingly works borderlessly.
          </Typography>
        </Paper>

        {/* The Blitz T2 Thesis */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            The Blitz T2 Thesis
          </Typography>
          <Typography variant="h5" sx={{ fontStyle: 'italic', mb: 3, opacity: 0.95 }}>
            "If value can be represented digitally, the world's treasury flows should move at the speed of information."
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.8, opacity: 0.9 }}>
            Blitz T2 introduces a unified balance layer—the Blitz Balance—a programmable USD-denominated 
            account backed 1:1 by USDC in qualified custody. By structuring all value changes as purchases 
            or redemptions of a digital asset, Blitz T2 achieves instant liquidity, interoperable transfers, 
            and regulatory clarity—abstracting away the complexity of global money movement for end users.
          </Typography>
        </Paper>

        {/* System Architecture */}
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          System Architecture
        </Typography>

        {/* Blitz Balance Components */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AccountBalanceIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" fontWeight="bold" color="primary">
              The Blitz Balance
            </Typography>
          </Box>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            Every user account holds a Blitz Balance—a USD-denominated representation of USDC 
            held in qualified custody. This creates a programmable money primitive with full 
            auditability and instant settlement capabilities.
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Component</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {balanceComponents.map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell sx={{ fontWeight: 'medium' }}>{row.component}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.description}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Key Properties:
            </Typography>
            <Typography variant="body2" component="div">
              • Fully atomic double-entry bookkeeping<br />
              • Internal transfers via custodian book entries—zero gas, instant finality<br />
              • Legally segregated custodian sub-accounts for transparent ownership
            </Typography>
          </Box>
        </Paper>

        {/* Treasury Layer */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SyncAltIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" fontWeight="bold" color="primary">
              Treasury Layer
            </Typography>
          </Box>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            The Treasury Layer orchestrates equilibrium between fiat inflows/outflows and custodial 
            USDC holdings, functioning as a market participant rather than a money transmitter.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, bgcolor: 'success.light', borderRadius: 1, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Inflow (Deposit Event)
                </Typography>
                <Typography variant="body2">
                  User deposits → Treasury purchases USDC via partner exchange → Balance credited
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, bgcolor: 'warning.light', borderRadius: 1, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Outflow (Withdrawal Event)
                </Typography>
                <Typography variant="body2">
                  User withdraws → Treasury sells USDC → Fiat disbursement issued
                </Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Stabilization Protocol:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Treasury maintains target ratio (e.g., 80% USDC custody / 20% fiat liquidity buffer) 
              for optimal operational efficiency
            </Typography>
          </Box>
        </Paper>

        {/* Universal Transmitter */}
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            Universal Transmitter Layer
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            The transmitter layer abstracts value movement across internal and external surfaces, 
            creating a universal money graph with USDC as the core settlement asset.
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, border: '2px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                  Internal Transfers
                </Typography>
                <Box sx={{ '& > div': { mb: 1.5 } }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Method:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Custodian book-entry update
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Latency:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      &lt; 500 milliseconds
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Cost:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      $0 transaction fees
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 3, border: '2px solid', borderColor: 'secondary.main', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom fontWeight="bold" color="secondary">
                  External Transfers
                </Typography>
                <Box sx={{ '& > div': { mb: 1.5 } }}>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">USDC On-Chain:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Gas auto-abstracted and optimized
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Fiat Ramps:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      ACH, Wire, PayPal, regional partners
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">Settlement:</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Real-time to T+1 depending on rail
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Secured Charge Card */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, bgcolor: 'grey.50' }}>
          <Typography variant="h5" gutterBottom color="primary" fontWeight="bold">
            BlitzPay Power Card
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, color: 'text.secondary' }}>
            The BlitzPay Power Card extends Blitz Balance utility into global commerce, providing 
            a secured charge card backed by USDC collateral.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Typography variant="body1" fontWeight="bold">Secured Charge Card</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" color="text.secondary">Collateral</Typography>
                <Typography variant="body1" fontWeight="bold">USDC-backed Balance</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" color="text.secondary">LTV Limit</Typography>
                <Typography variant="body1" fontWeight="bold">85–90%</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'grey.300' }}>
                <Typography variant="subtitle2" color="text.secondary">Rewards</Typography>
                <Typography variant="body1" fontWeight="bold">USDC Rebates</Typography>
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Risk Model:
            </Typography>
            <Typography variant="body2">
              • Collateralized balances ensure near-zero loss risk<br />
              • Automatic liquidation after grace period<br />
              • No interest charges (Reg Z exempt)<br />
              • Dynamic LTV adjustment based on deposit velocity and behavioral data
            </Typography>
          </Box>
        </Paper>

        {/* Ecosystem Integration */}
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          Ecosystem Integration
        </Typography>
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Product</strong></TableCell>
                  <TableCell><strong>Function</strong></TableCell>
                  <TableCell><strong>T2 Interface</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {integrations.map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>{row.product}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.function}</TableCell>
                    <TableCell>
                      <Chip label={row.interface} size="small" variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold">
              Outcome: A continuous capital cycle—revenue → liquidity → spending → reinvestment—all on a single programmable ledger.
            </Typography>
          </Box>
        </Paper>

        {/* Economic Model */}
        <Typography variant="h4" gutterBottom fontWeight="bold" sx={{ mb: 4 }}>
          Economic Model
        </Typography>
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Revenue Stream</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell align="right"><strong>Est. Spread</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revenueStreams.map((row, index) => (
                  <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                    <TableCell sx={{ fontWeight: 'medium' }}>{row.stream}</TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{row.description}</TableCell>
                    <TableCell align="right">
                      <Chip label={row.spread} color="primary" size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Note:</strong> Reward costs (~1%) are funded from interchange revenue and float yield, 
              creating a sustainable economic model.
            </Typography>
          </Box>
        </Paper>

        {/* Global Impact */}
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Global Impact
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3, bgcolor: 'success.light', borderRadius: 1, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Financial Inclusion
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Workers in emerging markets can receive income in USDC, maintain stable USD exposure, 
                  and spend globally via Visa/Mastercard—without needing a traditional bank account.
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3, bgcolor: 'info.light', borderRadius: 1, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Cross-Border Efficiency
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Traditional remittances: T+3 days + ~7% fees<br />
                  <strong>Blitz T2: T+0 + &lt;0.5% spread</strong>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 3, bgcolor: 'warning.light', borderRadius: 1, height: '100%' }}>
                <Typography variant="h6" gutterBottom fontWeight="bold">
                  Network Effects
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  Every enterprise using BlitzPay feeds liquidity into the Treasury. 
                  Treasury scale drives down spreads and enhances card reward economics.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Compliance Framework */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, borderLeft: '5px solid', borderColor: 'primary.main' }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Compliance Framework
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Custody & Transmission
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Licensed trust partner (e.g., Anchorage Digital)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Issuer Obligations
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Partner bank handles Reg E / disputes
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Blitz Role
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Program manager + software layer (non-MTL)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  AML/KYC
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Sardine + TRM Labs + OFAC screening
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Market Validation */}
        <Paper elevation={3} sx={{ p: 4, mb: 6, bgcolor: 'grey.50' }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Market Validation
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  6.2%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average cross-border payment fees (World Bank 2024)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  60%+
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Stablecoin adoption YoY growth (CoinMetrics Q2 2025)
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 2 }}>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  $1.1T
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Global remittance market by 2027
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Vision */}
        <Paper elevation={3} sx={{ p: 4, mb: 6 }}>
          <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
            Vision 2026–2030
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Full Global Access
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Non-U.S. users onboarded via compliant local partners and regional custodians
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Real-Time FX
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dynamic conversion between USDC, EURC, JPY digital assets, and other stablecoins
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Network Lending
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Over-collateralized credit lines built on top of Blitz Balance infrastructure
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Open API Layer
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  White-label treasury solutions for enterprises and governments
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Regulated Digital Bank Charter
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Long-term optionality: "Blitz Bank" for comprehensive financial services
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Conclusion */}
        <Paper
          elevation={4}
          sx={{
            p: 5,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            color: 'white'
          }}
        >
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Conclusion
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8, mb: 3, opacity: 0.95 }}>
            Blitz T2 unifies three historically separate financial domains: payments infrastructure, 
            treasury operations, and consumer financial experience. By grounding value in USDC, 
            maintaining qualified custody, and abstracting global compliance through partner banks 
            and regulated custodians, Blitz T2 transforms fragmented financial systems into a 
            cohesive programmable layer.
          </Typography>
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 4 }}>
            Blitz T2 — Where digital work becomes global money.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: 'primary.main',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': { bgcolor: 'grey.100' }
              }}
              onClick={() => navigate('/onboard')}
            >
              Start Building on Blitz T2
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: 'white',
                color: 'white',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
              }}
              onClick={() => window.open('https://docs.google.com/document/d/1AVIdsWcoS252eCWejPdzX4MZ73WtsbRLROQBcc2VCzY/edit?usp=sharing', '_blank')}
            >
              Technical Documentation
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 4, mt: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="body2" align="center">
            © 2025 Blitz Systems Corp - Blitz T2 - Transmitter & Treasury Layer. All rights reserved.
          </Typography>
          <Typography variant="body2" align="center" sx={{ mt: 1, opacity: 0.7 }}>
            <a href="https://www.useblitz.co/t2" style={{ color: 'white', textDecoration: 'none' }}>
              www.useblitz.co
            </a>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default BlitzT2Landing;